import moment from "moment";
import { ReportChartPage } from "./ReportChartPage";
import { ReportIndicatorCard } from "./ReportIndicatorCard";
import { ReportIndicatorCardGroup } from "./ReportIndicatorCardGroup";
import { ReportIndicatorCardSequencing } from "./ReportIndicatorCardSequencing";
import { ReportMainPage } from "./ReportMainPage";
import { ReportPageTemplate } from "./ReportPageTemplate";
import { sortIndicatorCardsBasedOnIndicatorGroup } from "../utils/sortIndicatorCards";

export const PdfPagesRenderer = ({
  actionSdkResponse,
  chartPageRef,
  consistency1PageRef,
  consistency2PageRef,
  efficiency1PageRef,
  firstPageReportData,
  golfClub,
  isTest = false,
  mainPageRef,
  speedPage1Ref,
  speedPage2Ref,
  userName,
  videoIds,
}) => {
  return (
    <>
      {/* Offscreen render root for PDF pages (hidden from UI but still rendered for html2canvas) */}
      <div
        aria-hidden
        className="font-manrope pointer-events-none select-none"
        style={{
          left: isTest ? "500px" : "-10000px",
          position: "absolute",
          top: isTest ? "0" : "-10000px",
          zIndex: isTest ? 1000 : -1000,
        }}
      >
        {firstPageReportData && (
          <>
            <ReportMainPage
              club={golfClub}
              clubSpeed={`${Number(firstPageReportData.clubSpeed ?? 0).toFixed(
                0
              )} mph`}
              consistencyScore={Number(
                firstPageReportData.consistencyScore ?? 0
              ).toFixed(0)}
              date={moment().format("DD MMM, YYYY")}
              descriptionSections={firstPageReportData.descriptionSections}
              efficiencyScore={Number(
                firstPageReportData.efficiencyScore ?? 0
              ).toFixed(0)}
              overallScore={Number(
                firstPageReportData.overallScore ?? 0
              ).toFixed(0)}
              pageRef={mainPageRef}
              speedScore={Number(firstPageReportData.speedScore ?? 0).toFixed(
                0
              )}
              swingsNumber={videoIds.length}
              swingType={firstPageReportData.swingType}
              totalPages={7}
              userName={userName}
            />
            <ReportChartPage pageRef={chartPageRef} totalPages={7} />
          </>
        )}

        {actionSdkResponse && (
          <>
            {/* Speed Report 1 */}
            <ReportPageTemplate
              club={golfClub}
              date={moment().format("MM/DD/YYYY")}
              firstData={{
                name: "Speed Score",
                value: Number(
                  actionSdkResponse?.speedPercentile
                    ?.clubHeadSpeedPercentileScore ?? 0
                ).toFixed(0),
              }}
              indicatorBlocks={[
                actionSdkResponse?.speedPercentile?.rotROMGroup,
                actionSdkResponse?.speedPercentile?.horizontalsGroup,
                actionSdkResponse?.speedPercentile?.verticalsGroup,
              ].map((block, i) => (
                <ReportIndicatorCardGroup
                  cards={sortIndicatorCardsBasedOnIndicatorGroup(
                    block?.name,
                    block
                  ).map((indicator, j) => (
                    <ReportIndicatorCard
                      chartReferenceArea={[
                        indicator?.indicatorRange -
                          indicator?.indicatorStDeviation,
                        indicator?.indicatorRange +
                          indicator?.indicatorStDeviation,
                      ]}
                      chartValues={indicator?.graph}
                      color={indicator?.zScore?.color?.i2_1?.toLowerCase?.()}
                      indicatorName={indicator?.name}
                      indicatorValue={indicator?.value}
                      key={`indicator-${j}`}
                      percentage={indicator?.percentileRank}
                      showAdditionalData
                      showChart
                      showIcons
                      tourRange={`${(
                        indicator?.indicatorRange -
                        indicator?.indicatorStDeviation
                      ).toFixed(1)} to ${(
                        indicator?.indicatorRange +
                        indicator?.indicatorStDeviation
                      ).toFixed(1)}`}
                      unit={indicator?.unit}
                      zScore={indicator?.zScore?.zScore}
                    />
                  ))}
                  groupName={block?.name}
                  key={`block-${i}`}
                  percentage={
                    block?.avgPercentile ||
                    block?.avgConsistencyScore ||
                    block?.avgEfficiencyScore ||
                    0
                  }
                />
              ))}
              page={3}
              pageName="Speed Report"
              pageRef={speedPage1Ref}
              secondData={{
                name: "Avg Club Head Speed",
                value: `${Number(
                  actionSdkResponse?.speedPercentile
                    ?.clubHeadSpeedAvgIndicatorValue ?? 0
                ).toFixed(0)} mph`,
              }}
              totalPages={7}
              userName={userName}
            />

            {/* Speed Report 2 */}
            <ReportPageTemplate
              club={golfClub}
              date={moment().format("MM/DD/YYYY")}
              firstData={{
                name: "Speed Score",
                value: Number(
                  actionSdkResponse?.speedPercentile
                    ?.clubHeadSpeedPercentileScore ?? 0
                ).toFixed(0),
              }}
              indicatorBlocks={[
                actionSdkResponse?.speedPercentile?.rotSpdGroup,
                actionSdkResponse?.speedPercentile?.releaseGroup,
              ].map((block, i) => (
                <ReportIndicatorCardGroup
                  cards={sortIndicatorCardsBasedOnIndicatorGroup(
                    block.name,
                    block
                  ).map((indicator, j) => (
                    <ReportIndicatorCard
                      chartReferenceArea={[
                        indicator.indicatorRange -
                          indicator.indicatorStDeviation,
                        indicator.indicatorRange +
                          indicator.indicatorStDeviation,
                      ]}
                      chartValues={indicator.graph}
                      color={indicator?.zScore?.color?.i2_1?.toLowerCase?.()}
                      indicatorName={indicator.name}
                      indicatorValue={indicator.value}
                      key={`indicator-${j}`}
                      percentage={indicator.percentileRank}
                      showAdditionalData
                      showChart
                      showIcons
                      tourRange={`${(
                        indicator.indicatorRange -
                        indicator.indicatorStDeviation
                      ).toFixed(1)} to ${(
                        indicator.indicatorRange +
                        indicator.indicatorStDeviation
                      ).toFixed(1)}`}
                      unit={indicator.unit}
                      zScore={indicator?.zScore?.zScore}
                    />
                  ))}
                  groupName={block.name}
                  key={`block-${i}`}
                  percentage={
                    block.avgPercentile ||
                    block.avgConsistencyScore ||
                    block.avgEfficiencyScore ||
                    0
                  }
                />
              ))}
              page={4}
              pageName="Speed Report"
              pageRef={speedPage2Ref}
              secondData={{
                name: "Avg Club Head Speed",
                value: `${Number(
                  actionSdkResponse.speedPercentile
                    .clubHeadSpeedAvgIndicatorValue ?? 0
                ).toFixed(0)} mph`,
              }}
              totalPages={7}
              userName={userName}
            />

            {/* Efficiency Report 1 */}
            <ReportPageTemplate
              club={golfClub}
              date={moment().format("MM/DD/YYYY")}
              firstData={{
                name: "Efficiency Score",
                value: Number(
                  actionSdkResponse.efficiencyScore.efficiencyScore ?? 0
                ).toFixed(0),
              }}
              indicatorBlocks={[
                actionSdkResponse.efficiencyScore.contributionGroup,
                actionSdkResponse.efficiencyScore.gainFactorGroup,
                actionSdkResponse.efficiencyScore.sequencingGroup,
              ].map((block, i) => (
                <ReportIndicatorCardGroup
                  cards={
                    block.name === "Sequencing"
                      ? [
                          <ReportIndicatorCardSequencing
                            indicatorName="Transition Order"
                            indicatorsArray={block.indicatorGroup}
                            key="seq1"
                            percentage={Number(
                              actionSdkResponse.efficiencyScore
                                .transitionOrderSequenceScore ?? 0
                            )}
                            valueToBaseColorOn={
                              actionSdkResponse.efficiencyScore
                                .transitionOrderSequenceScore
                            }
                          />,
                          <ReportIndicatorCardSequencing
                            indicatorName="Pelvis Movement Order"
                            indicatorsArray={block.indicatorGroup}
                            key="seq2"
                            percentage={Number(
                              actionSdkResponse.efficiencyScore
                                .movementSequenceOrderScore ?? 0
                            )}
                            valueToBaseColorOn={
                              actionSdkResponse.efficiencyScore
                                .movementSequenceOrderScore
                            }
                          />,
                        ]
                      : sortIndicatorCardsBasedOnIndicatorGroup(
                          block.name,
                          block
                        ).map((indicator, j) => (
                          <ReportIndicatorCard
                            chartReferenceArea={[
                              indicator.indicatorRange -
                                indicator.indicatorStDeviation,
                              indicator.indicatorRange +
                                indicator.indicatorStDeviation,
                            ]}
                            chartValues={indicator.graph}
                            color={indicator?.zScore?.color?.i2_1?.toLowerCase?.()}
                            indicatorName={indicator.name}
                            indicatorValue={indicator.value}
                            key={`indicator-${j}`}
                            percentage={indicator.percentileRank}
                            showAdditionalData
                            showChart
                            showIcons
                            tourRange={`${(
                              indicator.indicatorRange -
                              indicator.indicatorStDeviation
                            ).toFixed(1)} to ${(
                              indicator.indicatorRange +
                              indicator.indicatorStDeviation
                            ).toFixed(1)}`}
                            unit={indicator.unit}
                            zScore={indicator?.zScore?.zScore}
                          />
                        ))
                  }
                  groupName={block.name}
                  key={`block-${i}`}
                  percentage={
                    block.avgPercentile ||
                    block.avgConsistencyScore ||
                    block.avgEfficiencyScore ||
                    0
                  }
                />
              ))}
              page={5}
              pageName="Efficiency Report"
              pageRef={efficiency1PageRef}
              secondData={{}} // keeps layout logic
              totalPages={7}
              userName={userName}
            />

            {/* Consistency Report 1 */}
            <ReportPageTemplate
              club={golfClub}
              date={moment().format("MM/DD/YYYY")}
              firstData={{
                name: "Consistency Score",
                value: Number(
                  actionSdkResponse.consistencyScore.consistencyScore ?? 0
                ).toFixed(0),
              }}
              indicatorBlocks={[
                actionSdkResponse.consistencyScore.adrConsistencyGroup,
                actionSdkResponse.consistencyScore.topConsistencyGroup,
              ].map((block, i) => (
                <ReportIndicatorCardGroup
                  cards={sortIndicatorCardsBasedOnIndicatorGroup(
                    block.name,
                    block
                  ).map((indicator, j) => (
                    <ReportIndicatorCard
                      additionalDataText="Std. Dev"
                      chartReferenceArea={[
                        indicator.indicatorRange -
                          indicator.indicatorStDeviation,
                        indicator.indicatorRange +
                          indicator.indicatorStDeviation,
                      ]}
                      chartValues={indicator.graph}
                      color={indicator?.zScore?.color?.i2_1?.toLowerCase?.()}
                      indicatorName={indicator.name}
                      indicatorValue={indicator?.playerStd}
                      indicatorValueDecimalPlaces={1}
                      key={`indicator-${j}`}
                      percentage={indicator.percentileRank}
                      showAdditionalData={false}
                      showChart={false}
                      showIcons={false}
                      tourRange={`${(
                        indicator.indicatorRange -
                        indicator.indicatorStDeviation
                      ).toFixed(1)} to ${(
                        indicator.indicatorRange +
                        indicator.indicatorStDeviation
                      ).toFixed(1)}`}
                      unit={indicator.unit}
                      zScore={indicator?.zScore?.zScore}
                    />
                  ))}
                  groupName={block.name}
                  key={`block-${i}`}
                  percentage={
                    block.avgPercentile ||
                    block.avgConsistencyScore ||
                    block.avgEfficiencyScore ||
                    0
                  }
                />
              ))}
              page={6}
              pageName="Consistency Report"
              pageRef={consistency1PageRef}
              secondData={{
                name: "Swings Taken",
                value: `#${videoIds.length}`,
              }}
              totalPages={7}
              userName={userName}
            />

            {/* Consistency Report 2 */}
            <ReportPageTemplate
              club={golfClub}
              date={moment().format("MM/DD/YYYY")}
              firstData={{
                name: "Consistency Score",
                value: Number(
                  actionSdkResponse.consistencyScore.consistencyScore ?? 0
                ).toFixed(0),
              }}
              indicatorBlocks={[
                actionSdkResponse.consistencyScore.transitionConsistencyGroup,
                actionSdkResponse.consistencyScore.impConsistencyGroup,
              ].map((block, i) => (
                <ReportIndicatorCardGroup
                  cards={sortIndicatorCardsBasedOnIndicatorGroup(
                    block.name,
                    block
                  ).map((indicator, j) => (
                    <ReportIndicatorCard
                      additionalDataText="Std. Dev"
                      chartReferenceArea={[
                        indicator.indicatorRange -
                          indicator.indicatorStDeviation,
                        indicator.indicatorRange +
                          indicator.indicatorStDeviation,
                      ]}
                      chartValues={indicator.graph}
                      color={indicator?.zScore?.color?.i2_1?.toLowerCase?.()}
                      indicatorName={indicator.name}
                      indicatorValue={indicator?.playerStd}
                      indicatorValueDecimalPlaces={
                        block.name === "Impact" ? 1 : undefined
                      }
                      key={`indicator-${j}`}
                      percentage={indicator.percentileRank}
                      showAdditionalData={false}
                      showChart={false}
                      showIcons={false}
                      tourRange={`${(
                        indicator.indicatorRange -
                        indicator.indicatorStDeviation
                      ).toFixed(1)} to ${(
                        indicator.indicatorRange +
                        indicator.indicatorStDeviation
                      ).toFixed(1)}`}
                      unit={indicator.unit}
                      zScore={indicator?.zScore?.zScore}
                    />
                  ))}
                  groupName={block.name}
                  key={`block-${i}`}
                  percentage={
                    block.avgPercentile ||
                    block.avgConsistencyScore ||
                    block.avgEfficiencyScore ||
                    0
                  }
                />
              ))}
              page={7}
              pageName="Consistency Report"
              pageRef={consistency2PageRef}
              secondData={{
                name: "Swings Taken",
                value: `#${videoIds.length}`,
              }}
              totalPages={7}
              userName={userName}
            />
          </>
        )}
      </div>
    </>
  );
};
